import { buildMaSet, buildTenSet, normKey, stamp } from '@/modules/test-data/testDataSeedUtils'

export const UI_TEST_CREATE_COUNT = 2

export type EntityUiTestItem = {
  ma: string
  ten: string
  ghiChu?: string
}

export type EntityUiTestPlan = {
  createItems: EntityUiTestItem[]
  probeMa: string | null
  probeId: string | null
  deleteMa: string | null
}

function newRunToken(): string {
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`
}

export function generateEntityCreateItems(
  maTokenPrefix: string,
  existingMa: Set<string>,
  existingTen: Set<string>,
  count = UI_TEST_CREATE_COUNT,
): EntityUiTestItem[] {
  const runId = newRunToken()
  const maSet = new Set(existingMa)
  const tenSet = new Set(existingTen)
  const items: EntityUiTestItem[] = []
  let suffix = 1

  while (items.length < count && suffix < count + 100) {
    const ma = `${maTokenPrefix}${runId}-${suffix}`
    const ten = `Test UI ${runId} — ${suffix}`
    const mk = normKey(ma)
    const tk = normKey(ten)
    if (!maSet.has(mk) && !tenSet.has(tk)) {
      items.push({
        ma,
        ten,
        ghiChu: `Sinh khi chạy test — ${new Date().toLocaleString('vi-VN')}`,
      })
      maSet.add(mk)
      tenSet.add(tk)
    }
    suffix += 1
  }

  return items
}

export function rowMa(
  row: Record<string, unknown>,
  maKey: string,
): string {
  const v = row[maKey]
  return v != null ? String(v).trim() : ''
}

export function rowTen(row: Record<string, unknown>, tenKey: string): string {
  const v = row[tenKey]
  return v != null ? String(v).trim() : ''
}

export function rowId(row: Record<string, unknown>): string {
  return String(row.id ?? '')
}

function pickProbeMa(
  rows: Array<Record<string, unknown>>,
  maKey: string,
  maTokenPrefix: string,
  createItems: EntityUiTestItem[],
  excludeMa?: string | null,
): string | null {
  const exclude = normKey(excludeMa ?? '')
  const withMa = rows.filter((r) => {
    const ma = rowMa(r, maKey)
    return ma && normKey(ma) !== exclude
  })
  if (withMa.length === 0) return null

  const uit = withMa.filter((r) => rowMa(r, maKey).startsWith(maTokenPrefix))
  if (uit.length > 0) return rowMa(uit[uit.length - 1], maKey)
  return rowMa(withMa[withMa.length - 1], maKey)
}

function firstExistingMa(
  rows: Array<Record<string, unknown>>,
  maKey: string,
  items: EntityUiTestItem[],
): string | null {
  for (const item of items) {
    if (rows.some((r) => normKey(rowMa(r, maKey)) === normKey(item.ma))) return item.ma
  }
  return null
}

export function buildEntityUiTestPlan(
  rows: Array<Record<string, unknown>>,
  maKey: string,
  tenKey: string,
  maTokenPrefix: string,
): EntityUiTestPlan {
  const existingMa = buildMaSet(rows.map((r) => ({ ma: rowMa(r, maKey) })))
  const existingTen = buildTenSet(rows.map((r) => ({ tenNhom: rowTen(r, tenKey) })))
  const createItems = generateEntityCreateItems(maTokenPrefix, existingMa, existingTen)

  const probeMa =
    firstExistingMa(rows, maKey, createItems.slice(0, 1)) ??
    pickProbeMa(rows, maKey, maTokenPrefix, createItems) ??
    createItems[0]?.ma ??
    null

  const deleteMa =
    firstExistingMa(rows, maKey, createItems.slice(1, 2)) ??
    createItems[1]?.ma ??
    pickProbeMa(rows, maKey, maTokenPrefix, createItems, probeMa) ??
    null

  const probeRow = probeMa ? rows.find((r) => normKey(rowMa(r, maKey)) === normKey(probeMa)) : null

  return {
    createItems,
    probeMa,
    probeId: probeRow ? rowId(probeRow) || null : null,
    deleteMa,
  }
}

export function refreshEntityUiTestPlan(
  plan: EntityUiTestPlan,
  rows: Array<Record<string, unknown>>,
  maKey: string,
  maTokenPrefix: string,
): void {
  const createdProbe = firstExistingMa(rows, maKey, plan.createItems.slice(0, 1))
  const createdDelete = firstExistingMa(rows, maKey, plan.createItems.slice(1, 2))

  plan.probeMa =
    createdProbe ??
    pickProbeMa(rows, maKey, maTokenPrefix, plan.createItems) ??
    plan.createItems[0]?.ma ??
    plan.probeMa

  const probeRow = plan.probeMa
    ? rows.find((r) => normKey(rowMa(r, maKey)) === normKey(plan.probeMa ?? ''))
    : null
  plan.probeId = probeRow ? rowId(probeRow) || null : plan.probeId

  plan.deleteMa =
    createdDelete ??
    (plan.createItems[1]?.ma && normKey(plan.createItems[1].ma) !== normKey(plan.probeMa ?? '')
      ? plan.createItems[1].ma
      : null) ??
    pickProbeMa(rows, maKey, maTokenPrefix, plan.createItems, plan.probeMa) ??
    plan.deleteMa
}

export function generateEditTen(existingTen: Set<string>, label = 'sửa'): string {
  const token = stamp()
  let suffix = 0
  while (suffix < 50) {
    const ten = suffix === 0 ? `UIT ${label} ${token}` : `UIT ${label} ${token}-${suffix}`
    if (!existingTen.has(normKey(ten))) return ten
    suffix += 1
  }
  return `UIT ${label} ${token}-${Date.now()}`
}

export function describeEntityUiTestPlan(plan: EntityUiTestPlan): string {
  const create = plan.createItems.map((i) => i.ma).join(', ') || '—'
  return `Thêm: [${create}] · Tìm/sửa: ${plan.probeMa ?? '—'} · Xóa: ${plan.deleteMa ?? '—'}`
}
